import React from "react";

import { TFunction } from "utils";

import { PRACTICE_SLUG, PracticeSlug } from "models";

import { Checkbox, FieldsCountSummary, Flex, Icon, Tooltip, Typography } from "components";

type PlaceholderAwareRow = {
  key?: string;
  isPlaceholderRow?: boolean;
  missingPracticeColumns?: Partial<Record<string, boolean>>;
};

export type ExpandableColumnInfo = {
  // only shown when at least one row actually has more than one entry for this practice
  hasMultiple: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
};

// static column config for AllFieldsDataTable - pulled out since it's mostly translation lookups + widths and
// has nothing to do with row/selection state, aside from the header checkbox needing the "select all" wiring
export const getAllFieldsDataTableColumns = (
  t: TFunction,
  {
    isSelectAllChecked,
    onSelectAllClicked,
    showMineralFertilizers,
    showBaselineColumn,
    visibleFieldsCount,
    totalFieldsCount,
    enrolledFieldsCount,
    enrolledFieldsTotalHectares,
    fieldsSizeSummary,
    hasFiltersActive,
    expandableColumns,
    highlightedMissingRowKey,
    isEnrollmentDataLoading,
  }: {
    // true once every currently-eligible row is selected, not just "some row is selected"
    isSelectAllChecked: boolean;
    onSelectAllClicked: (checked: boolean) => void;
    showMineralFertilizers: boolean;
    showBaselineColumn: boolean;
    visibleFieldsCount: number;
    totalFieldsCount: number;
    enrolledFieldsCount: number;
    enrolledFieldsTotalHectares: number;
    // e.g. "2186.4 ha (100%)" for the currently visible fields, null when the farm has no known total size
    fieldsSizeSummary: string | null;
    hasFiltersActive: boolean;
    expandableColumns: Partial<Record<PracticeSlug, ExpandableColumnInfo>>;
    highlightedMissingRowKey: string | null;
    isEnrollmentDataLoading: boolean;
  }
) => {
  // toggle only shows on hover, and only for a practice with a row that actually has more than one entry
  const buildExpandableTitle = (label: string, info: ExpandableColumnInfo | undefined) => (
    <Flex align="center" justify="space-between" className="all-fields-data-table-view-expandable-column-title">
      <Typography variant="paragraph-s" color="grey-600" className="margin-0">
        {label}
      </Typography>
      {info?.hasMultiple && (
        <Tooltip
          variant="neutral"
          title={t(info.isExpanded ? "allFieldsDataTableView:table.header.collapseAll" : "allFieldsDataTableView:table.header.expandAll")}
        >
          <Icon
            className={`all-fields-data-table-view-column-expand-toggle ${info.isExpanded ? "expanded" : ""}`}
            dataTestId={`all-fields-data-table-view-expand-${label}`}
            iconName={info.isExpanded ? "tabler/fold-vertical" : "tabler/unfold-vertical"}
            size="s"
            onClick={info.onToggleExpand}
          />
        </Tooltip>
      )}
    </Flex>
  );

  const columns = [
    {
      title: (
        <Flex align="center" justify="flex-start">
          <Checkbox
            className="historical-practice-data-table-view-select-checkbox"
            checked={isSelectAllChecked}
            onChange={(event) => onSelectAllClicked(event.target.checked)}
          />
        </Flex>
      ),
      dataIndex: "select",
      key: "select",
      width: "3rem",
      fixed: "left" as const,
      className: "all-fields-data-table-view-select-cell",
    },
    {
      title: (
        <Flex vertical gap="0.25rem">
          <FieldsCountSummary
            visibleFieldsCount={visibleFieldsCount}
            totalFieldsCount={totalFieldsCount}
            hasFiltersActive={hasFiltersActive}
            fieldsSizeSummary={fieldsSizeSummary}
            enrolledFieldsCount={enrolledFieldsCount}
            enrolledFieldsTotalHectares={enrolledFieldsTotalHectares}
            isEnrollmentDataLoading={isEnrollmentDataLoading}
          />
        </Flex>
      ),
      dataIndex: "field",
      key: "field",
      width: "18rem",
      className: "all-fields-data-table-view-field-cell",
      fixed: "left" as const,
    },
    {
      title: t("historicalPracticeDataTableView:table.header.status"),
      dataIndex: "status",
      key: "status",
      width: "5rem",
      fixed: "left" as const,
      className: "all-fields-data-table-view-status-cell",
    },
    {
      title: t("allFieldsDataTableView:table.header.cropName"),
      dataIndex: PRACTICE_SLUG.CROPS,
      key: PRACTICE_SLUG.CROPS,
      width: "12rem",
      className: "all-fields-data-table-view-crop-cell",
      fixed: "left" as const,
    },
    { title: t("historicalPracticeDataTableView:table.header.growingPeriod"), dataIndex: "growingPeriod", key: "growingPeriod", width: "10rem" },
    {
      title: buildExpandableTitle(t(`historicalPracticeDataTableView:table.header.${PRACTICE_SLUG.SEEDINGS}`), expandableColumns[PRACTICE_SLUG.SEEDINGS]),
      dataIndex: PRACTICE_SLUG.SEEDINGS,
      key: PRACTICE_SLUG.SEEDINGS,
      width: "10rem",
    },
    {
      title: <div style={{ textAlign: "left" }}>{t("allFieldsDataTableView:table.header.yield")}</div>,
      dataIndex: "yield",
      key: "yield",
      width: "10rem",
    },
    {
      title: buildExpandableTitle(t(`historicalPracticeDataTableView:table.header.${PRACTICE_SLUG.TILLAGES}`), expandableColumns[PRACTICE_SLUG.TILLAGES]),
      dataIndex: PRACTICE_SLUG.TILLAGES,
      key: PRACTICE_SLUG.TILLAGES,
      width: "12rem",
    },
    {
      title: buildExpandableTitle(
        t(`historicalPracticeDataTableView:table.header.${PRACTICE_SLUG.ORGANIC_FERTILIZERS}`),
        expandableColumns[PRACTICE_SLUG.ORGANIC_FERTILIZERS]
      ),
      dataIndex: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      key: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      width: "10rem",
    },
    ...(showMineralFertilizers
      ? [
          {
            title: buildExpandableTitle(
              t(`historicalPracticeDataTableView:table.header.${PRACTICE_SLUG.MINERAL_FERTILIZERS}`),
              expandableColumns[PRACTICE_SLUG.MINERAL_FERTILIZERS]
            ),
            dataIndex: PRACTICE_SLUG.MINERAL_FERTILIZERS,
            key: PRACTICE_SLUG.MINERAL_FERTILIZERS,
            width: "10rem",
          },
        ]
      : []),
    ...(showBaselineColumn
      ? [
          {
            title: t("allFieldsDataTableView:table.header.baseline"),
            dataIndex: "baseline",
            key: "baseline",
            width: "10rem",
          },
        ]
      : []),
  ];

  // a placeholder row (field with no crop for the selected year, PE-821) only fills in "select" + "field" +
  // "status" + "crops" (all fixed-left, unaffected — "crops" renders its own "+ Add crop" action in
  // AllFieldsDataTable) — "growingPeriod" is the first scrollable column, so every column from there onward
  // collapses into one spanning blank cell (a colSpan can't cross the fixed/scrollable boundary), except
  // "baseline", which isn't tied to a crop existing and renders its own content independently
  const spanStartIndex = columns.findIndex((column) => column.key === "growingPeriod");
  const baselineColumnIndex = columns.findIndex((column) => column.key === "baseline");
  const spanEndIndex = baselineColumnIndex === -1 ? columns.length : baselineColumnIndex;

  return columns.map((column, index) => {
    const getSpanProps = (record: PlaceholderAwareRow) => {
      if (index < spanStartIndex || index >= spanEndIndex || !record.isPlaceholderRow) return {};
      return index === spanStartIndex ? { colSpan: spanEndIndex - spanStartIndex } : { colSpan: 0 };
    };
    return {
      ...column,
      onCell: (record: PlaceholderAwareRow) => ({
        ...getSpanProps(record),
        ...(record.key === highlightedMissingRowKey && record.missingPracticeColumns?.[column.key]
          ? { className: "all-fields-data-table-view-cell-highlighted" }
          : {}),
      }),
    };
  });
};
